import React, { useState, useEffect } from "react";
import {  Paper, Typography, Box, Button, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import InvestorsTable from "../Tables/InvestorsTable";
import { useDispatch, useSelector } from "react-redux";
import { GetStrategyVariance, Taxlots } from "../../Redux/Actions/transactionService";
import { useParams } from "react-router-dom";
import Loader from "../../Loader";
import { useSelectedUserId } from "../../SelectedUserIdContext";

function Portfolio({ onSelectionChange, setShowManual, setShowAuto }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { strategyData, loading} = useSelector((state) => state.transaction);
  const { selectedUserIds } = useSelectedUserId();
  const [strategy, setStrategy] = useState([]);

  useEffect(() => {
    if (id) {
      dispatch(GetStrategyVariance({  strategyId: id}));
      dispatch(Taxlots({  strategyId: id }))
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (strategyData && strategyData.length > 0) {
      const filteredStrategyData = selectedUserIds.length > 0
        ? strategyData.filter(item => selectedUserIds.includes(item?.userRole?.id))
        : strategyData;
      
      const assetIdGroups = {};
      filteredStrategyData.forEach(strategyItem => {
        strategyItem.assetsVariance.forEach(assets => {
          const assetId = assets?.asset?.id;
          if (!assetIdGroups[assetId]) {
            assetIdGroups[assetId] = {
              AssetName: assets?.asset?.name,
              CurrentPortfolio: 0,
              TotalPortfolio: 0,
              IdealWeight: assets.idealWeight,
              ProposedAllocation: 0,
              pos: 0,
              neg: 0,
            };
          }
          assetIdGroups[assetId].CurrentPortfolio += assets.currentPortfolio;
          assetIdGroups[assetId].TotalPortfolio += strategyItem.totalPortfolio;
          if (assets.variance > 0) {
            assetIdGroups[assetId].pos += assets.variance;
          } else if (assets.variance < 0) {
            assetIdGroups[assetId].neg += assets.variance;
          }
        });
      });
  
      const sumArray = Object.keys(assetIdGroups).map(assetId => ({
        AssetId: assetId,
        ...assetIdGroups[assetId],
        CurrentAllocation: (assetIdGroups[assetId].CurrentPortfolio / assetIdGroups[assetId].TotalPortfolio) * 100,
        ProposedAllocation: (assetIdGroups[assetId].IdealWeight * assetIdGroups[assetId].TotalPortfolio) / 100,
        Variance: assetIdGroups[assetId].CurrentPortfolio - (assetIdGroups[assetId].IdealWeight * assetIdGroups[assetId].TotalPortfolio) / 100,
        Variance$: assetIdGroups[assetId].IdealWeight - (assetIdGroups[assetId].CurrentPortfolio / assetIdGroups[assetId].TotalPortfolio) * 100,
      }));
      setStrategy(sumArray);
    } else {
      setStrategy([]);
    }
  }, [strategyData, selectedUserIds]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : strategy.length > 0 ? (
        <Box sx={{ p: 3 }}>
          {/* Asset Variance Table */}
          <Paper sx={{ 
            mb: 3, 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
          }}>
            <Box sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2.5, 
                  color: '#1A1A1A',
                  fontSize: '16px',
                  fontWeight: 500
                }}
              >
                Asset Variance
              </Typography>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: '#F8F9FA',
                      '& th': {
                        borderBottom: 'none',
                      }
                    }}>
                      {/* Asset column - outside sections */}
                      <TableCell 
                        sx={{ 
                          backgroundColor: '#F8F9FA',
                          borderRight: '1px solid #E0E0E0',
                          width: '15%',
                          color: '#666666',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        Asset
                      </TableCell>
                      
                      {/* ALLOCATION Section */}
                      <TableCell 
                        colSpan={2} 
                        align="center"
                        sx={{ 
                          backgroundColor: '#F8F9FA',
                          borderRight: '1px solid #E0E0E0',
                          color: '#666666',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        ALLOCATION
                      </TableCell>

                      {/* PORTFOLIO Section */}
                      <TableCell 
                        colSpan={3} 
                        align="center"
                        sx={{ 
                          backgroundColor: '#F8F9FA',
                          borderRight: '1px solid #E0E0E0',
                          color: '#666666',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        PORTFOLIO
                      </TableCell>

                      {/* REQUIRED ACTIVITY FOR ASSETS Section */}
                      <TableCell 
                        colSpan={2} 
                        align="center"
                        sx={{ 
                          backgroundColor: '#F8F9FA',
                          color: '#666666',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}
                      >
                        REQUIRED ACTIVITY FOR ASSETS
                      </TableCell>
                    </TableRow>

                    {/* Subheaders */}
                    <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                      {/* Asset column */}
                      <TableCell sx={{ 
                        borderRight: '1px solid #E0E0E0',
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        Asset
                      </TableCell>

                      {/* ALLOCATION columns */}
                      <TableCell sx={{ 
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        Exis. Allocation
                      </TableCell>
                      <TableCell sx={{ 
                        borderRight: '1px solid #E0E0E0',
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        New Allocation
                      </TableCell>

                      {/* PORTFOLIO columns */}
                      <TableCell sx={{ 
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        Exis. Portfolio
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        Ideal Portfolio
                      </TableCell>
                      <TableCell sx={{ 
                        borderRight: '1px solid #E0E0E0',
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        % Variance
                      </TableCell>

                      {/* REQUIRED ACTIVITY columns */}
                      <TableCell sx={{ 
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        Assets to Buy
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#333333',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}>
                        Assets to Sell
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {strategy.map((row) => (
                      <TableRow 
                        key={row.AssetId}
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          '&:hover': { backgroundColor: '#F8F9FA' },
                          '& td': {
                            borderBottom: '1px solid #E0E0E0',
                          }
                        }}
                      >
                        {/* Asset column */}
                        <TableCell sx={{ 
                          borderRight: '1px solid #E0E0E0',
                          fontSize: '13px',
                          color: '#333333'
                        }}>
                          {row.AssetName}
                        </TableCell>

                        {/* ALLOCATION section */}
                        <TableCell sx={{ fontSize: '13px', color: '#333333' }}>
                          {!isFinite(row.CurrentAllocation) || isNaN(row.CurrentAllocation) 
                            ? '-' 
                            : `${row.CurrentAllocation?.toFixed(2)}%`}
                        </TableCell>
                        <TableCell sx={{ 
                          borderRight: '1px solid #E0E0E0',
                          fontSize: '13px',
                          color: '#333333'
                        }}>
                          {!isFinite(row.IdealWeight) || isNaN(row.IdealWeight)
                            ? '-'
                            : `${row.IdealWeight?.toFixed(2)}%`}
                        </TableCell>

                        {/* PORTFOLIO section */}
                        <TableCell sx={{ fontSize: '13px', color: '#333333' }}>
                          {!isFinite(row.CurrentPortfolio) || isNaN(row.CurrentPortfolio) || row.CurrentPortfolio === 0
                            ? '-'
                            : `$${row.CurrentPortfolio?.toFixed(2)}`}
                        </TableCell>
                        <TableCell sx={{ fontSize: '13px', color: '#333333' }}>
                          {!isFinite(row.ProposedAllocation) || isNaN(row.ProposedAllocation) || row.ProposedAllocation === 0
                            ? '-'
                            : `$${row.ProposedAllocation?.toFixed(2)}`}
                        </TableCell>
                        <TableCell sx={{ 
                          borderRight: '1px solid #E0E0E0',
                          fontSize: '13px',
                          color: isFinite(row.Variance$) && !isNaN(row.Variance$) && row.Variance$ > 0 ? '#2E7D32' : '#D32F2F'
                        }}>
                          {!isFinite(row.Variance$) || isNaN(row.Variance$)
                            ? '-'
                            : `${row.Variance$?.toFixed(2)}%`}
                        </TableCell>

                        {/* REQUIRED ACTIVITY section */}
                        <TableCell sx={{ 
                          fontSize: '13px',
                          color: '#2E7D32',
                          fontWeight: row.pos > 0 ? 500 : 400
                        }}>
                          {!isFinite(row.pos) || isNaN(row.pos) || row.pos <= 0 ? '-' : `$${row.pos?.toFixed(2)}`}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: '13px',
                          color: '#D32F2F',
                          fontWeight: row.neg < 0 ? 500 : 400
                        }}>
                          {!isFinite(row.neg) || isNaN(row.neg) || row.neg >= 0 ? '-' : `$${Math.abs(row.neg)?.toFixed(2)}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>

          {/* Investors Table */}
          <Paper sx={{ mt: 3, borderRadius: '8px', overflow: 'hidden' }}>
            <Box sx={{ p: 2 }}>
              <InvestorsTable
                data={strategyData}
                onSelectionChange={onSelectionChange}
                setShowManual={setShowManual}
                setShowAuto={setShowAuto}
              />
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh" textAlign="center" flexDirection="column">
          <Paper sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <img src="https://via.placeholder.com/150" alt="No Data" style={{ width: '150px', marginBottom: '16px' }} />
            <Typography variant="h5" component="div" gutterBottom>
              No user data found to rebalance
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Please check back later or contact support if you think this is an error.
            </Typography>
            <Button variant="contained" color="primary">
              Take Action
            </Button>
          </Paper>
        </Box>
      )}
    </>
  );
}

export default Portfolio;
